import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../../utils/dateFormatter";
import { getCaseStateLabel, CaseState } from "../../types/case";
import type { Case } from "../../types/case";
import { useCaseInbox } from "../../hooks/useCases";

interface NotificationItem {
  id: string;
  type: "assignment" | "status_change" | "comment" | "due_date" | "escalation";
  caseId: number;
  caseTitle: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  priority?: string;
  status?: CaseState;
  actorName?: string;
  rule?: string;
  chapter?: string;
}

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

// Map notification types onto design-system token pairs using color-mix() for
// soft tints so avatar surfaces stay consistent with the rest of the shell.
const getNotificationTone = (
  type: string,
): { bg: string; fg: string; rail: string } => {
  switch (type) {
    case "escalation":
      return {
        bg: "color-mix(in srgb, var(--risk) 12%, var(--bg-sunken))",
        fg: "var(--risk)",
        rail: "var(--risk)",
      };
    case "due_date":
      return {
        bg: "color-mix(in srgb, var(--warn) 12%, var(--bg-sunken))",
        fg: "var(--warn)",
        rail: "var(--warn)",
      };
    case "status_change":
      return {
        bg: "color-mix(in srgb, var(--ok) 12%, var(--bg-sunken))",
        fg: "var(--ok)",
        rail: "var(--ok)",
      };
    case "comment":
      return {
        bg: "color-mix(in srgb, var(--accent) 10%, var(--bg-sunken))",
        fg: "var(--accent)",
        rail: "var(--accent)",
      };
    case "assignment":
    default:
      return {
        bg: "color-mix(in srgb, var(--accent) 12%, var(--bg-sunken))",
        fg: "var(--accent)",
        rail: "var(--accent)",
      };
  }
};

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "assignment":
      return "👤";
    case "status_change":
      return "📋";
    case "comment":
      return "💬";
    case "escalation":
      return "⚠️";
    case "due_date":
      return "📅";
    default:
      return "🔔";
  }
};

// Determine the most relevant notification for a case based on its current state
const classifyCase = (
  caseItem: Case,
): { type: NotificationItem["type"]; message: string } => {
  const state = caseItem.state as CaseState;

  if (
    state === CaseState.Escalated ||
    state === CaseState.ComplianceEscalated
  ) {
    return { type: "escalation", message: "Case has been escalated" };
  }

  if (caseItem.dueDate) {
    const dueDate = new Date(caseItem.dueDate);
    const now = new Date();
    const daysUntilDue = Math.ceil(
      (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysUntilDue < 0) {
      return {
        type: "due_date",
        message: `Overdue by ${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) !== 1 ? "s" : ""}`,
      };
    }
    if (daysUntilDue <= 7) {
      return {
        type: "due_date",
        message:
          daysUntilDue === 0
            ? "Due today"
            : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
      };
    }
  }

  if (caseItem.assignedTo) {
    const assigneeName =
      caseItem.assignedTo.name ||
      `${caseItem.assignedTo.firstName} ${caseItem.assignedTo.lastName}`.trim() ||
      caseItem.assignedTo.email ||
      "someone";
    return {
      type: "assignment",
      message: `Assigned to ${assigneeName}`,
    };
  }

  if (caseItem.commentCount && caseItem.commentCount > 0) {
    return {
      type: "comment",
      message: `${caseItem.commentCount} comment${caseItem.commentCount !== 1 ? "s" : ""}`,
    };
  }

  if (state !== CaseState.Open) {
    return {
      type: "status_change",
      message: `Status: ${getCaseStateLabel(state)}`,
    };
  }

  return { type: "status_change", message: "New case created" };
};

// Transform cases to notification items — one notification per case
const transformCasesToNotifications = (cases: Case[]): NotificationItem[] => {
  return cases.map((caseItem) => {
    const { type, message } = classifyCase(caseItem);

    const rule =
      caseItem.complianceNodeTitle ||
      (caseItem.sourceRuleId ? `Rule ${caseItem.sourceRuleId}` : undefined);

    return {
      id: `${caseItem.id}-${type}`,
      type,
      caseId: caseItem.id,
      caseTitle: caseItem.title,
      message,
      timestamp: caseItem.updatedAt || caseItem.createdAt,
      isRead: false,
      priority: caseItem.priority,
      status: caseItem.state as CaseState,
      rule,
    };
  });
};

const filterAndSortNotifications = (
  notifications: NotificationItem[],
  showUnreadOnly: boolean,
  filterType: string,
  sortBy: "newest" | "oldest" | "priority",
): NotificationItem[] => {
  let filtered = notifications;

  if (showUnreadOnly) {
    filtered = filtered.filter((n) => !n.isRead);
  }

  if (filterType !== "all") {
    filtered = filtered.filter((n) => n.type === filterType);
  }

  return [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return (
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      case "oldest":
        return (
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      case "priority": {
        const priorityOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
        const aPriority =
          priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
        const bPriority =
          priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
        return bPriority - aPriority;
      }
      default:
        return 0;
    }
  });
};

// Inline SVG icons — aria-hidden, sized at 12px inline
const FilterSvg = () => (
  <svg
    aria-hidden="true"
    width="11"
    height="11"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    style={{ display: "inline", verticalAlign: "middle", marginRight: 3 }}
  >
    <path d="M2 4h12M4 8h8M6 12h4" />
  </svg>
);

const SortSvg = () => (
  <svg
    aria-hidden="true"
    width="11"
    height="11"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    style={{ display: "inline", verticalAlign: "middle", marginRight: 3 }}
  >
    <path d="M2 5h12M2 8h8M2 11h5" />
  </svg>
);

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "5px 8px",
  borderRadius: "var(--r-sm)",
  border: "1px solid var(--line)",
  background: "var(--bg-elev)",
  color: "var(--ink-1)",
  fontSize: 12,
  cursor: "pointer",
  outline: "none",
  appearance: "auto",
};

const labelCapStyle: React.CSSProperties = {
  display: "block",
  marginBottom: 4,
  fontSize: 11,
  fontWeight: 600,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: inboxData, isLoading: loading } = useCaseInbox(
    {},
    { enabled: open },
  );

  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [filterType, setFilterType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "priority">(
    "newest",
  );

  // Track narrow viewports so the panel can switch to a bottom-sheet layout.
  // We re-evaluate on resize + orientationchange so rotating a device or
  // resizing a desktop window keeps the layout in sync. matchMedia may be
  // absent in some test environments (e.g. jsdom), so we guard accordingly.
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return false;
    return window.matchMedia("(max-width: 600px)").matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const evaluate = () =>
      setIsMobile(window.matchMedia("(max-width: 600px)").matches);
    window.addEventListener("resize", evaluate);
    window.addEventListener("orientationchange", evaluate);
    return () => {
      window.removeEventListener("resize", evaluate);
      window.removeEventListener("orientationchange", evaluate);
    };
  }, []);

  // TODO: Remove localStorage implementation once BE API for marking notifications as read is available
  const [readNotifications, setReadNotifications] = useState<Set<string>>(
    () => {
      const stored = localStorage.getItem("readNotifications");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    },
  );

  // Click-outside dismiss
  useEffect(() => {
    if (!anchorEl) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        e.target !== anchorEl
      )
        onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [anchorEl, onClose]);

  // Escape key dismiss
  useEffect(() => {
    if (!anchorEl) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [anchorEl, onClose]);

  // Derive notifications from cached cases data
  const notifications = useMemo(() => {
    const cases = inboxData?.cases ?? [];
    const items = transformCasesToNotifications(cases);
    return items.map((n) => ({
      ...n,
      isRead: readNotifications.has(n.id),
    }));
  }, [inboxData?.cases, readNotifications]);

  const handleNotificationClick = (notification: NotificationItem) => {
    // TODO: Remove localStorage logic once BE API for marking as read is available
    const updatedReadNotifications = new Set(readNotifications);
    updatedReadNotifications.add(notification.id);
    setReadNotifications(updatedReadNotifications);
    localStorage.setItem(
      "readNotifications",
      JSON.stringify(Array.from(updatedReadNotifications)),
    );

    navigate(`/cases/${notification.caseId}`);
    onClose();
  };

  const handleMarkAllAsRead = () => {
    // TODO: Remove localStorage logic once BE API for marking all as read is available
    const allIds = new Set(notifications.map((n) => n.id));
    const merged = new Set([...readNotifications, ...allIds]);
    setReadNotifications(merged);
    localStorage.setItem(
      "readNotifications",
      JSON.stringify(Array.from(merged)),
    );
  };

  const filteredNotifications = filterAndSortNotifications(
    notifications,
    showUnreadOnly,
    filterType,
    sortBy,
  );

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const desktopPanelWidth = Math.min(420, window.innerWidth - 16);
  // Clamp the right offset so the panel can never overflow the left edge of
  // the viewport on narrow desktops or tablets — keeps at least 8px of gutter.
  const rawPanelRight = window.innerWidth - rect.right;
  const desktopPanelRight = Math.max(
    8,
    Math.min(rawPanelRight, window.innerWidth - desktopPanelWidth - 8),
  );
  const desktopPanelTop = rect.bottom + 8;
  const desktopPanelMaxHeight = Math.min(
    600,
    window.innerHeight - rect.bottom - 24,
  );

  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        top: "auto",
        width: "100%",
        maxHeight: "85vh",
        background: "var(--bg-elev)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-md) var(--r-md) 0 0",
        boxShadow: "var(--sh-pop)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 1300,
      }
    : {
        position: "fixed",
        top: desktopPanelTop,
        right: desktopPanelRight,
        width: desktopPanelWidth,
        maxHeight: desktopPanelMaxHeight,
        background: "var(--bg-elev)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-md)",
        boxShadow: "var(--sh-pop)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 1300,
      };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Notifications"
      style={panelStyle}
    >
      {isMobile && (
        <div
          role="presentation"
          aria-hidden="true"
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 8,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: "var(--ink-4)",
            }}
          />
        </div>
      )}{" "}
      {/* ── Header ── */}
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid var(--line)",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: "var(--ink-1)",
            lineHeight: 1.25,
          }}
        >
          Notifications
        </h2>
        {unreadCount > 0 && (
          <span
            aria-live="polite"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: 18,
              minWidth: 18,
              padding: "0 6px",
              borderRadius: 9,
              background:
                "color-mix(in srgb, var(--accent) 14%, var(--bg-sunken))",
              color: "var(--accent)",
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
              border:
                "1px solid color-mix(in srgb, var(--accent) 28%, transparent)",
            }}
          >
            {unreadCount}
          </span>
        )}
      </div>
      {/* ── Controls bar ── */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--line)",
          background: "var(--bg-sunken)",
          flexShrink: 0,
        }}
      >
        {/* Row 1: unread toggle + mark all */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              userSelect: "none",
              fontSize: 13,
              fontWeight: 500,
              color: "var(--ink-1)",
            }}
          >
            {/* Pill switch implemented with a styled checkbox */}
            <span
              style={{
                position: "relative",
                display: "inline-block",
                width: 32,
                height: 18,
                flexShrink: 0,
              }}
            >
              <input
                type="checkbox"
                role="switch"
                aria-label="Show unread only"
                aria-checked={showUnreadOnly}
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: "100%",
                  height: "100%",
                  margin: 0,
                  cursor: "pointer",
                  zIndex: 1,
                }}
              />
              <span
                style={{
                  display: "block",
                  width: 32,
                  height: 18,
                  borderRadius: 9,
                  background: showUnreadOnly
                    ? "var(--accent)"
                    : "var(--bg-sunken)",
                  border: "1.5px solid",
                  borderColor: showUnreadOnly ? "var(--accent)" : "var(--line)",
                  transition: "background 0.18s, border-color 0.18s",
                  position: "relative",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    left: showUnreadOnly ? 14 : 2,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: showUnreadOnly
                      ? "var(--bg-elev)"
                      : "var(--ink-4)",
                    transition: "left 0.18s, background 0.18s",
                  }}
                />
              </span>
            </span>
            Only show unread
          </label>

          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              style={{
                background: "none",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-sm)",
                padding: "3px 8px",
                fontSize: 11,
                fontWeight: 600,
                color: "var(--accent)",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Row 2: filter + sort selects */}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginTop: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 140px" }}>
            <label htmlFor="np-filter" style={labelCapStyle}>
              <FilterSvg />
              Filter
            </label>
            <select
              id="np-filter"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={selectStyle}
            >
              <option value="all">All Types</option>
              <option value="assignment">👤 Assignments</option>
              <option value="status_change">📋 Status Changes</option>
              <option value="comment">💬 Comments</option>
              <option value="escalation">⚠️ Escalations</option>
              <option value="due_date">📅 Due Dates</option>
            </select>
          </div>

          <div style={{ flex: "1 1 140px" }}>
            <label htmlFor="np-sort" style={labelCapStyle}>
              <SortSvg />
              Sort
            </label>
            <select
              id="np-sort"
              value={sortBy}
              onChange={(e) =>
                setSortBy(e.target.value as "newest" | "oldest" | "priority")
              }
              style={selectStyle}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="priority">By Priority</option>
            </select>
          </div>
        </div>
      </div>
      {/* ── List area ── */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {loading ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 24px",
              gap: 12,
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: "3px solid var(--line)",
                borderTopColor: "var(--accent)",
                animation: "np-spin 0.7s linear infinite",
                display: "block",
              }}
            />
            <style>{`@keyframes np-spin { to { transform: rotate(360deg); } }`}</style>
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
              Loading notifications…
            </span>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 24px",
              textAlign: "center",
            }}
          >
            <span
              role="img"
              aria-label="Bell"
              style={{ fontSize: 48, lineHeight: 1, marginBottom: 12 }}
            >
              🔔
            </span>
            <h3
              style={{
                margin: "0 0 6px",
                fontSize: 15,
                fontWeight: 600,
                color: "var(--ink-2)",
              }}
            >
              No notifications
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
              {showUnreadOnly
                ? "You're all caught up! No unread notifications."
                : "You don't have any notifications at the moment."}
            </p>
          </div>
        ) : (
          <>
            {/* Count bar */}
            <div
              aria-live="polite"
              style={{
                padding: "8px 16px",
                background: "var(--bg-sunken)",
                borderBottom: "1px solid var(--line)",
                fontSize: 11,
                color: "var(--ink-3)",
              }}
            >
              Showing {filteredNotifications.length} of {notifications.length}{" "}
              notifications
            </div>

            {/* Notification items */}
            {filteredNotifications.map((notification, index) => {
              const tone = getNotificationTone(notification.type);

              // Priority pill styling
              const priorityBg =
                notification.priority === "CRITICAL"
                  ? "color-mix(in srgb, var(--risk) 12%, var(--bg-sunken))"
                  : notification.priority === "HIGH"
                    ? "color-mix(in srgb, var(--warn) 12%, var(--bg-sunken))"
                    : notification.priority === "MEDIUM"
                      ? "color-mix(in srgb, var(--accent) 10%, var(--bg-sunken))"
                      : "var(--bg-sunken)";
              const priorityColor =
                notification.priority === "CRITICAL"
                  ? "var(--risk)"
                  : notification.priority === "HIGH"
                    ? "var(--warn)"
                    : notification.priority === "MEDIUM"
                      ? "var(--accent)"
                      : "var(--ink-3)";

              return (
                <React.Fragment key={notification.id}>
                  <button
                    type="button"
                    aria-label={`${notification.caseTitle} — ${notification.message}`}
                    onClick={() => handleNotificationClick(notification)}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 12,
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      borderLeft: `3px solid ${notification.isRead ? "transparent" : tone.rail}`,
                      background: notification.isRead
                        ? "transparent"
                        : "var(--bg-sunken)",
                      padding: "12px 16px 12px 13px",
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        "var(--bg-sunken)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        notification.isRead
                          ? "transparent"
                          : "var(--bg-sunken)";
                    }}
                  >
                    {/* Avatar */}
                    <span
                      aria-hidden="true"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: tone.bg,
                        color: tone.fg,
                        border: "1px solid var(--line)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 16,
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        marginTop: 1,
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </span>

                    {/* Content */}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {/* Case title */}
                      <span
                        style={{
                          display: "block",
                          fontSize: 14,
                          fontWeight: notification.isRead ? 500 : 600,
                          color: "var(--ink-1)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {notification.caseTitle}
                      </span>

                      {/* Message */}
                      <span
                        style={{
                          display: "block",
                          fontSize: 12,
                          color: "var(--ink-3)",
                          marginTop: 2,
                        }}
                      >
                        {notification.message}
                      </span>

                      {/* Metadata row */}
                      <span
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          marginTop: 6,
                          fontSize: 11,
                        }}
                      >
                        <span>
                          <span style={{ color: "var(--ink-3)" }}>ID: </span>
                          <span
                            style={{
                              fontFamily: "var(--font-mono)",
                              color: "var(--accent)",
                              fontWeight: 600,
                            }}
                          >
                            {String(notification.caseId).substring(0, 8)}
                          </span>
                        </span>

                        {notification.status !== undefined && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <span style={{ color: "var(--ink-3)" }}>
                              Status:{" "}
                            </span>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "1px 6px",
                                borderRadius: "var(--r-sm)",
                                background:
                                  "color-mix(in srgb, var(--accent) 10%, var(--bg-sunken))",
                                color: "var(--accent)",
                                fontSize: 10,
                                fontWeight: 500,
                                lineHeight: "16px",
                              }}
                            >
                              {getCaseStateLabel(notification.status)}
                            </span>
                          </span>
                        )}

                        {notification.rule && (
                          <span>
                            <span style={{ color: "var(--ink-3)" }}>
                              Rule:{" "}
                            </span>
                            <span
                              style={{
                                color: "var(--ink-1)",
                                fontWeight: 600,
                              }}
                            >
                              {notification.rule}
                            </span>
                          </span>
                        )}
                      </span>

                      {/* Bottom row: timestamp + priority */}
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                          {formatDateTime(notification.timestamp)}
                        </span>

                        {notification.priority && (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "1px 6px",
                              borderRadius: "var(--r-sm)",
                              border: "1px solid var(--line)",
                              background: priorityBg,
                              color: priorityColor,
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              lineHeight: "16px",
                            }}
                          >
                            {notification.priority}
                          </span>
                        )}
                      </span>
                    </span>

                    {/* Arrow indicator */}
                    <span
                      aria-hidden="true"
                      style={{
                        fontSize: 14,
                        color: "var(--ink-3)",
                        flexShrink: 0,
                        alignSelf: "center",
                      }}
                    >
                      →
                    </span>
                  </button>

                  {index < filteredNotifications.length - 1 && (
                    <div
                      style={{
                        height: 1,
                        background: "var(--line)",
                        margin: 0,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>
      {/* ── Footer ── */}
      {filteredNotifications.length > 0 && (
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--line)",
            background: "var(--bg-elev)",
          }}
        >
          <button
            type="button"
            onClick={() => {
              navigate("/cases");
              onClose();
            }}
            style={{
              display: "block",
              width: "100%",
              background: "none",
              border: "1px solid var(--line)",
              borderRadius: "var(--r-sm)",
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--accent)",
              cursor: "pointer",
              textAlign: "center",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                "color-mix(in srgb, var(--accent) 6%, var(--bg-elev))";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "none";
            }}
          >
            View all open cases →
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
